/**
 * Per-J-code sibling order evaluation predicate for Step 5 of the pipeline.
 * @module siblingOrderFilter/evaluateSiblingAgainstJCode
 */
import { SiblingOrder } from "../../../data-model/siblingOrdersModel";
import { MedicalPaOrdersAuthStatus } from "../../../enums/medicalPaOrdersAuthStatus";
import { describeOrder, logger } from "./logging";
import {
  isOrderDosWithinRange,
  isOrderInParentMonthYear,
} from "./orderPredicates";
import { FilterJCodeInput } from "./types";

/**
 * The outcome of evaluating a single sibling order against a single parent
 * J-code's authorization constraints.
 *
 * @property passed - Whether the sibling order satisfies this J-code's checks
 * @property reason - Human-readable rejection reason (present when `passed` is
 *   `false`), used for debug logging
 */
export interface JCodeCheckResult {
  passed: boolean;
  reason?: string;
}

/**
 * Evaluates whether a single sibling order satisfies one parent J-code's
 * authorization constraints. This is a read-only predicate — it does not
 * consume the J-code's remaining-visit budget.
 *
 * - `no_auth_required`: the order must fall in the parent's service month/year.
 * - `auth_on_file` / `auth_by_risa`: the order's date of service must fall
 *   within the J-code's auth window and there must still be sibling visit
 *   budget left after reserving one visit for the parent order.
 * - any other status: always passes (no additional constraint).
 *
 * @param sibling - The sibling order being evaluated
 * @param jCode - The parent J-code whose auth status drives the checks
 * @param parentOrderDos - The parent order's date of service reference point
 * @param acceptedForJCode - How many sibling orders have already been accepted
 *   for this J-code in the current Step 5 pass (does not include the order
 *   currently being evaluated). The parent order itself is assumed to consume
 *   one visit from `visits_remaining`, so the sibling budget is
 *   `max(visits_remaining - 1, 0)`.
 * @returns A {@link JCodeCheckResult} describing whether the order passed and,
 *   if not, why
 */
export const evaluateSiblingAgainstJCode = (
  sibling: SiblingOrder,
  jCode: FilterJCodeInput,
  parentOrderDos: string,
  acceptedForJCode: number,
): JCodeCheckResult => {
  const step = `[Step 5][jcode=${jCode?.j_code ?? "n/a"} auth_status=${
    jCode?.auth_status ?? "unknown"
  }]`;

  switch (jCode?.auth_status) {
    case MedicalPaOrdersAuthStatus.NoAuthRequired: {
      if (!isOrderInParentMonthYear(parentOrderDos, sibling)) {
        return {
          passed: false,
          reason: `not in parent service month/year (parentDos=${parentOrderDos})`,
        };
      }
      logger.debug(
        `${step} PASSED check: in parent service month/year (parentDos=${parentOrderDos}): ${describeOrder(
          sibling,
        )}`,
      );
      return { passed: true };
    }
    case MedicalPaOrdersAuthStatus.AuthOnFile:
    case MedicalPaOrdersAuthStatus.AuthByRISA: {
      const startDate = jCode?.start_date ?? "";
      const endDate = jCode?.end_date ?? "";
      if (!isOrderDosWithinRange(sibling, startDate, endDate)) {
        return {
          passed: false,
          reason: `dos outside auth window (${startDate}..${endDate})`,
        };
      }
      logger.debug(
        `${step} PASSED check: dos within auth window (${startDate}..${endDate}): ${describeOrder(
          sibling,
        )}`,
      );
      // Orders are evaluated in ascending DOS order, so the earliest ones
      // consume the sibling-visit budget first. `visits_remaining` reflects the
      // auth's total remaining visits; the parent order reserves one of those,
      // leaving `siblingVisitBudget` slots for linked siblings.
      const remainingVisits = jCode?.visits_remaining ?? 0;
      const siblingVisitBudget =
        remainingVisits === 0 ? 0 : remainingVisits - 1;
      if (acceptedForJCode >= siblingVisitBudget) {
        return {
          passed: false,
          reason: `exceeds sibling visit budget (accepted=${acceptedForJCode}, sibling_budget=${siblingVisitBudget}, visits_remaining=${remainingVisits})`,
        };
      }
      logger.debug(
        `${step} PASSED check: within sibling visit budget (accepted=${acceptedForJCode} < sibling_budget=${siblingVisitBudget}, visits_remaining=${remainingVisits}): ${describeOrder(
          sibling,
        )}`,
      );
      return { passed: true };
    }
    default:
      // No additional constraint for any other auth status.
      logger.debug(
        `${step} PASSED check: no constraint for this auth status: ${describeOrder(
          sibling,
        )}`,
      );
      return { passed: true };
  }
};
